import React, { MouseEventHandler } from 'react'
import _ from 'underscore'

import { appendUrlParam } from '../appendUrlParam'
import LegacyNavigatorWrapper from '../helper/legacyNavigatorWrapper'
import Selector from './selector'

interface Props {
    type: string
    queryKey: string
    selected: string[] | null
    searchUrl: string
    navigator: LegacyNavigatorWrapper
    removable: boolean
    onRemove?: MouseEventHandler
    onCriteriaModified?: (values: { [queries: string]: string[] } & { preventLocalStorage: number }) => void
    openRight: boolean
}
interface State {
    mapping: {}
    recent: string[]
    search: Array<{ id: number; name: string }>
}
class OnlineDropdownSelector extends React.PureComponent<Props, State> {
    public static defaultProps = {
        removable: false,
        openRight: false,
    }

    public state: State = {
        mapping: {},
        recent: [],
        search: [],
    }

    /*eslint-disable react/sort-comp */
    // Rule seems broken for this case
    public delayedSearchTimeout: number | null = null

    public componentDidMount() {
        this.reload()
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (this.props.selected === prevProps.selected) {
            return
        }

        this.reload()
    }

    public reload = () => {
        const selectionQuery = (this.props.selected ?? [])
            .filter((value, index, self) => self.indexOf(value) === index)
            .map((value, index) => `id[]=` + value)
            .join('&')

        jQuery.ajax({
            url: appendUrlParam(this.props.searchUrl, 'mode=init' + (selectionQuery ? '&' + selectionQuery : '')),
            dataType: 'json',
            success: data => {
                const selectedIds = _.pluck(data.selected, 'id')
                const recentIds = _.pluck(data.recent, 'id')
                const searchIds = _.pluck(data.search, 'id')
                const selectedURecentIds = _.union(selectedIds, recentIds)
                const mapping = this.constructMapping([...data.selected, ...data.recent, ...data.search])

                // Partition items into recent and search
                // in a way that no item repeats and the priority
                // is recent > search
                this.setState({
                    recent: _.filter(recentIds, id => !_.contains(selectedIds, id)),
                    search: _.filter(searchIds, id => !_.contains(selectedURecentIds, id)),
                    mapping,
                })
            },
        })
    }

    public constructMapping = (items: Array<{ id: number; name: string }>) => {
        const mapping = this.state.mapping

        items.forEach(item => {
            mapping[item.id] = item.name
        })

        return mapping
    }

    public setSelected = selected => {
        const values = { [this.props.queryKey]: selected, preventLocalStorage: 1 }
        if (this.props.onCriteriaModified) {
            this.props.onCriteriaModified(values)
        }
        this.props.navigator.goTo(values)
    }

    public changeSelection = (area, id) => {
        const state: any = {}
        let selected: string[] = []
        let partitions: string[][] = []

        if (area === 'selected') {
            partitions = _.partition(this.props.selected ?? [], item => item !== id)
            selected = partitions[0]
            state.recent = _.uniq(_.union(this.state.recent, partitions[1]), false, item => item)
        } else {
            partitions = _.partition(this.state[area], item => item !== id)
            state[area] = partitions[0]
            selected = _.uniq(_.union(this.props.selected ?? [], partitions[1]), false, item => item)
        }

        this.setState(state)
        this.setSelected(selected)
    }

    public filterSearch = ev => {
        const search = ev.target.value

        if (this.delayedSearchTimeout) {
            clearTimeout(this.delayedSearchTimeout)
        }

        this.delayedSearchTimeout = window.setTimeout(
            searching => {
                jQuery.ajax({
                    url: appendUrlParam(this.props.searchUrl, 'mode=search&q=' + searching),
                    dataType: 'json',
                    success: response => {
                        const searchIds = _.pluck(response.search, 'id')
                        const selectedURecentIds = _.union(this.props.selected ?? [], this.state.recent)
                        const mapping = this.constructMapping(response.search)

                        this.setState({
                            search: _.without(searchIds, selectedURecentIds),
                            mapping,
                        })
                    },
                })
            },
            100,
            search,
        )
    }

    public renderList = (items, area) => {
        return (
            <ul>
                {_.map(items, item => {
                    const id = this.props.type + '-' + area + '-' + item

                    return (
                        <li key={id}>
                            <div className="checkbox checkbox-default">
                                <input
                                    type="checkbox"
                                    name="id[]"
                                    value={item}
                                    id={id}
                                    checked={area === 'selected'}
                                    onChange={() => this.changeSelection(area, item)}
                                />
                                <label htmlFor={id}>{this.state.mapping[item]}</label>
                            </div>
                        </li>
                    )
                })}
            </ul>
        )
    }

    public render() {
        let dropdownMenuStyle = { minWidth: '250px' }
        if (this.props.openRight) {
            dropdownMenuStyle = _.extend({}, dropdownMenuStyle, { left: '0', right: 'auto' })
        }

        return (
            <Selector
                type={this.props.type}
                mainClass="dropdown dropdown-search-select"
                hasCaret
                removable={this.props.removable}
                onRemove={this.props.onRemove}
                head={(selected => {
                    switch (selected.length) {
                        case 0:
                            return 'All ' + this.props.type + 's'
                        case 1: {
                            const first = _.first(selected) as string
                            return !_.isEmpty(this.state.mapping) && typeof this.state.mapping[first] !== 'undefined'
                                ? this.state.mapping[first].substring(0, 15) + '…'
                                : selected.length + ' ' + this.props.type
                        }
                        default:
                            return selected.length + ' ' + this.props.type + 's'
                    }
                })(this.props.selected || [])}
            >
                <div className="dropdown-menu" role="menu" aria-labelledby="dLabel" style={dropdownMenuStyle}>
                    <div className="errors-filter-search">
                        <form className="form">
                            <div className="form-group has-feedback">
                                <input onChange={this.filterSearch} className="form-control" type="text" />
                                <i className="form-control-feedback fa fa-search" />
                            </div>
                        </form>
                    </div>

                    <div className="divider" />

                    <div
                        style={{
                            minHeight: '100px',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: '8px',
                            paddingTop: '0px',
                        }}
                    >
                        {this.props.selected && this.props.selected.length > 0 ? (
                            <div className="dropdown-search-select-selected-panel">
                                <strong>Selected {this.props.type}s</strong>
                                {this.renderList(this.props.selected, 'selected')}
                            </div>
                        ) : (
                            ''
                        )}

                        {this.state.recent && this.state.recent.length > 0 ? (
                            <div>
                                <strong>Recent {this.props.type}s</strong>
                                {this.renderList(this.state.recent, 'recent')}
                            </div>
                        ) : (
                            ''
                        )}
                        {this.state.search && this.state.search.length > 0 ? (
                            <div>
                                <strong>All {this.props.type}s</strong>
                                {this.renderList(this.state.search, 'search')}
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                </div>
            </Selector>
        )
    }
}

export default OnlineDropdownSelector
